import { ApolloClient, ApolloError, from, InMemoryCache, Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import cookies from 'js-cookie';
import { createUploadLink } from 'apollo-upload-client';

import fetchRefreshToken, { RefreshTokenResponse } from 'config/apolloConfig/fetchRefreshToken';
import { rejectRefreshToken, resolveRefreshToken } from 'config/apolloConfig/refreshToken';
import webAppLogin, { LoginByTelegramWebAppResponse } from 'config/apolloConfig/webAppLogin';
import ROUTES from 'constants/routes';
import { COOKIE_NAME, webApp } from '@myhrbox/myhrbox-frontend-core';
import { getItem } from 'utils/cloudStorage';
import { setContext } from '@apollo/client/link/context';

type FailedRequest = {
  resolve: (token: string | null) => void;
  reject: (error: ApolloError | unknown) => void;
};

export const graphqlLink = `${process.env.REACT_APP_API_URL}/graphql`;

const isTelegram = webApp?.platform && webApp?.platform !== 'unknown';
let isRefreshing = false;
let failedRequestsQueue: FailedRequest[] = [];

const httpLink = createUploadLink({
  uri: graphqlLink,
  fetchOptions: {
    credentials: 'include',
  },
  credentials: 'include',
  headers: { 'Apollo-Require-Preflight': 'true' },
});

const getToken = async (key: string): Promise<string | undefined> => {
  if (isTelegram) {
    return getItem(key);
  }
  return cookies.get(key);
};

const authMiddleware = setContext(async (_, { headers = {} }) => {
  const token = await getToken(COOKIE_NAME.ACCESS_TOKEN);

  return {
    headers: {
      ...headers,
      authorization: token ? `JWT ${token}` : '',
    },
  };
});

export const processQueue = async (
  error: ApolloError | null | unknown,
  token: string | null | undefined = null,
) => {
  failedRequestsQueue.forEach((prom) => (error ? prom.reject(error) : prom.resolve(token)));
  failedRequestsQueue = [];
  isRefreshing = false;
};

const refreshAccessToken = async (): Promise<
  LoginByTelegramWebAppResponse | RefreshTokenResponse | null
> => {
  const accessToken = await getToken(COOKIE_NAME.ACCESS_TOKEN);
  const refreshToken = await getToken(COOKIE_NAME.REFRESH_TOKEN);

  if (accessToken && refreshToken) {
    return fetchRefreshToken(accessToken, refreshToken);
  }

  if (isTelegram) {
    return webAppLogin();
  }
  return null;
};

// eslint-disable-next-line consistent-return
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }
  if (networkError) console.log(`[Network error]: ${networkError}`);

  const isUnauthorized = graphQLErrors?.some(({ message }) => message === 'Unauthorized');
  const isLoginByTelegramTokenOperation = operation.operationName === 'loginByTelegramToken';
  const isLoginByTelegramWebAppTokenOperation =
    operation.operationName === 'loginByTelegramWebAppToken';
  const isLoginPage = window.location.pathname.includes(ROUTES.LOGIN);
  const isPublicExaminationPage = window.location.pathname.includes(ROUTES.PUBLIC_EXAMINATION);
  const isExaminationCheckPage = window.location.pathname.includes(ROUTES.EXAMINATION);

  if (
    isUnauthorized &&
    !isLoginByTelegramTokenOperation &&
    !isLoginByTelegramWebAppTokenOperation &&
    !isLoginPage &&
    !isPublicExaminationPage &&
    !isExaminationCheckPage
  ) {
    return new Observable((observer) => {
      // Кладем запрос в очередь
      // отклоненных запросов, там он будет ждать решения по обновлению токена
      new Promise((resolve, reject) => {
        failedRequestsQueue.push({ resolve, reject });
      })
        .then(() => {
          // Если все ок, то идем дальше, пуская вперед остальные запросы;
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };
          forward(operation).subscribe(subscriber);
        })
        .catch(() => {
          // Refresh-токен тоже просрочен,
          // редирект на авторизацию произведет первый запрос в очереди отклоненных
        });
      // Если данный запрос первый в очереди отклоненных,
      // то есть до него никто не поставил isRefreshing
      if (!isRefreshing) {
        isRefreshing = true;

        // Идем вручную на рефреш токена, ибо клиент Apollo
        // испорчен старым токеном до момента обновления
        refreshAccessToken()
          .then(async (data) => resolveRefreshToken(data))
          .catch(async (e) => rejectRefreshToken(e));
      }
    });
  }
});

const client = new ApolloClient({
  link: from([errorLink, authMiddleware, httpLink]),
  cache: new InMemoryCache(),
});

export default client;
